import { AUTH_GA_CARD } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';

type Extras = Record<string, unknown>;
type ApiResponse = {
    extra?: Extras & { card?: Record<string, unknown> };
    errors?: Record<string, unknown>;
};

type SearchParamsFieldValue = string | number | boolean;
type ApiFieldLike = {
    name: string;
    canonicalPath?: string | null;
    value?: SearchParamsFieldValue;
};
type ApiFieldCollectionLike = Record<string, ApiFieldLike> | ApiFieldLike[] | null | undefined;

type ToggleSpinner = (value: boolean) => Promise<unknown> | void;
type AddExtras = (extra: Extras) => Promise<unknown>;
type SetScreen = (screen: string) => void;
type SetScreenSuccess = () => void;
type ReplaceUrl = (id: string | number | undefined) => void;

type Card = {
    cardPocket?: { id?: string | number };
};

export const STEPS = 'steps';
export const PIN = 'pin';
export const ADDRESS = 'delivery_address';
export const CONFIRM = 'confirm';
export const PAYMENT_METHOD = 'payment_method';
export const SUCCESS = 'success';
export const FAILED = 'failed';
export const PREVIEW_LINK = 'preview_link';
export const LINK = 'link';
export const DELIVERY_STATUS = 'delivery_status';
export const CARD_ACTIVATE = 'card_activate';
export const SUCCESS_LINKED = 'success_linked';

const setScreenAndUpdateExtra = (json: ApiResponse, addExtras: AddExtras, setScreen?: SetScreen, setScreenSuccess?: SetScreenSuccess): void => {
    if (json.extra?.card && Object.keys(json.extra?.card).length > 0) {
        void addExtras(json.extra).then(() => {
            if (setScreenSuccess) {
                setScreenSuccess();
            } else {
                setScreen?.(SUCCESS);
            }
        });
    } else {
        setScreen?.(FAILED);
    }
};

export const requestOrderPlastic = ({
    fields,
    toggleSpinner,
    setScreenSuccess,
    setScreen,
    addExtras,
    url,
}: {
    fields: ApiFieldCollectionLike;
    toggleSpinner: ToggleSpinner;
    setScreenSuccess?: SetScreenSuccess;
    setScreen?: SetScreen;
    addExtras: AddExtras;
    url: string;
}): void => {
    void toggleSpinner(true);
    void apiFetchData<ApiResponse>(url, fields)
        .then((json) => setScreenAndUpdateExtra(json, addExtras, setScreen, setScreenSuccess))
        .finally(() => {
            void toggleSpinner(false);
        })
        .catch((error) => {
            logWarn(error);
        });
};
export const requestLinkCardToAccount = ({
    toggleSpinner,
    setScreenSuccess,
    setScreen,
    addExtras,
    url,
}: {
    toggleSpinner: ToggleSpinner;
    setScreenSuccess?: SetScreenSuccess;
    setScreen?: SetScreen;
    addExtras: AddExtras;
    url: string;
}) => {
    return (id: string | number): void => {
        void toggleSpinner(true);
        void apiFetchData<ApiResponse>(`${url}${id}`)
            .then((json) => setScreenAndUpdateExtra(json, addExtras, setScreen, setScreenSuccess))
            .finally(() => {
                void toggleSpinner(false);
            })
            .catch((error) => {
                logWarn(error);
            });
    };
};

export const requestsReissueCard = (card: Card, toggleSpinner: ToggleSpinner, addExtras: AddExtras, setScreen: SetScreen, replaceUrl?: ReplaceUrl): void => {
    void toggleSpinner(true);
    const getVirtualHref = `${AUTH_GA_CARD}get/virtual/${card?.cardPocket?.id}`;
    void apiFetchData<ApiResponse>(getVirtualHref)
        .then((json) => {
            if (json) {
                const hasNoErrors = Object.keys(json?.errors ?? {}).length === 0;
                if (hasNoErrors) {
                    if (replaceUrl) {
                        if (json.extra?.card && Object.keys(json.extra?.card).length > 0) {
                            void addExtras(json.extra).then(() => replaceUrl(json.extra?.card?.id as string | number | undefined));
                        }
                    } else {
                        setScreenAndUpdateExtra(json, addExtras, setScreen);
                    }
                }
            }
        })
        .catch((error) => logWarn(error))
        .finally(() => {
            void toggleSpinner(false);
        });
};

export const requestsIssueVirtualCard = (pocketId: string | number, toggleSpinner: ToggleSpinner, addExtras: AddExtras, setScreen: SetScreen): void => {
    void toggleSpinner(true);
    const getVirtualHref = `${AUTH_GA_CARD}get/virtual/${pocketId}`;
    void apiFetchData<ApiResponse>(getVirtualHref)
        .then((json) => {
            if (json) {
                const hasNoErrors = Object.keys(json?.errors ?? {}).length === 0;
                if (hasNoErrors) {
                    setScreenAndUpdateExtra(json, addExtras, setScreen);
                }
            }
        })
        .catch((error) => logWarn(error))
        .finally(() => {
            void toggleSpinner(false);
        });
};
